import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';
import {useTheme} from '@mui/material/styles';
import {countries} from 'wobi-web-common';
import FlagBadge, {CheckBadge} from './FlagBadge';
import AllDestinationsModal from './modals/AllDestinationsModal';
import popularCountries from '../assets/js/popularCountries';

/**
 * Derive continent codes from selected country English values.
 * Mirrors the logic from the original CountriesSelect component.
 */
const getSelectedContinents = (countriesSelected) => {
  const continents = new Set();
  countriesSelected.forEach((value) => {
    const fullValue = countries.find((c) => c.value === value);
    if (fullValue) continents.add(fullValue.continentInEnglish);
  });
  return Array.from(continents);
};

/**
 * Convert English country values to Hebrew names.
 */
const convertCountriesToHebrew = (countriesList) => countriesList.map((engName) => {
    const found = countries.find((c) => c.value === engName);
    return found ? found.label : '';
  });

/**
 * Get the display data for a country value.
 */
const getCountryDisplay = (countryValue) => {
  const found = countries.find((c) => c.value === countryValue);
  return {
    value: countryValue,
    label: found ? found.label : countryValue,
    imgSrc: found?.imgSrc,
  };
};

/**
 * DestinationGrid — Multi-select grid of popular destinations with flag circles.
 * Shows up to 11 popular destinations + a "more" tile that opens AllDestinationsModal.
 *
 * Props:
 * - value: string[] — currently selected country values
 * - onSelect(countries, continents, hebrewNames) — called on selection change
 * - error: boolean — shows error state
 */
const DestinationGrid = ({value = [], onSelect, error}) => {
  const intl = useIntl();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = React.useState(false);
  const selected = value || [];

  const toggleCountry = (countryValue) => {
    let newSelected;
    if (selected.includes(countryValue)) {
      newSelected = selected.filter((v) => v !== countryValue);
    } else {
      newSelected = [...selected, countryValue];
    }
    const continents = getSelectedContinents(newSelected);
    const hebrew = convertCountriesToHebrew(newSelected);
    onSelect(newSelected, continents, hebrew);
  };

  // Build the visible grid: selected items first (newest first), then popular fill remaining slots
  const gridIds = React.useMemo(() => {
    const chosen = [...selected].reverse();
    const rest = popularCountries.filter((id) => !chosen.includes(id));
    return [...chosen, ...rest].slice(0, 11);
  }, [selected]);

  const handleModalToggle = (countryValue) => {
    toggleCountry(countryValue);
  };

  return (
    <div data-testid='destinations-wrapper' data-validation-field='targetCountries'>
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'}}>
        <div className='rd-section-title'><FormattedMessage id='step1.tab1.targetTitle'/></div>
        <div className='rd-section-subtitle'>
          <FormattedMessage id='destinationGrid.subtitle'/>
        </div>
      </div>

      {error && (
        <div className='rd-error' style={{marginTop: 12}}>
          <FormattedMessage id='destinationGrid.error'/>
        </div>
      )}

      <div
        className='rd-dest-grid'
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '12px 2px',
          justifyItems: 'center',
          marginTop: 18,
        }}
      >
        {gridIds.map((countryValue) => {
          const display = getCountryDisplay(countryValue);
          const isSelected = selected.includes(countryValue);
          return (
            <button
              key={countryValue}
              type='button'
              onClick={() => toggleCountry(countryValue)}
              aria-pressed={isSelected}
              aria-label={display.label}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 9,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span style={{position: 'relative', display: 'inline-flex'}}>
                <FlagBadge src={display.imgSrc} size={56} selected={isSelected} />
                {isSelected && <CheckBadge size={20} />}
              </span>
              <span style={{
                fontSize: theme.typography.pxToRem(12.5),
                fontWeight: isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightSemiBold,
                color: isSelected ? theme.palette.primary.dark : theme.palette.text.secondary,
                textAlign: 'center',
                lineHeight: 1.25,
                maxWidth: 76,
              }}>
                {display.label}
              </span>
            </button>
          );
        })}

        {/* "More" tile */}
        <button
          type='button'
          onClick={() => setModalOpen(true)}
          aria-label={intl.formatMessage({id: 'destinationGrid.more'})}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 9,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <span style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            flexShrink: 0,
            background: theme.palette.action.selected,
            border: `2px dashed ${theme.palette.secondary.main}`,
            color: theme.palette.secondary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: theme.typography.pxToRem(32),
            fontWeight: theme.typography.fontWeightLight,
            lineHeight: 1,
            paddingBottom: 4,
          }}>
            +
          </span>
          <span style={{fontSize: theme.typography.pxToRem(12.5), fontWeight: theme.typography.fontWeightBold, color: theme.palette.secondary.main, textAlign: 'center', lineHeight: 1.25, maxWidth: 76}}>
            <FormattedMessage id='destinationGrid.more'/>
          </span>
        </button>
      </div>

      <AllDestinationsModal
        open={modalOpen}
        selected={selected}
        onToggle={handleModalToggle}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

DestinationGrid.propTypes = {
  error: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.array,
};

export {getCountryDisplay};
export default DestinationGrid;
